// `useConfirm()` → `confirm({...}): Promise<boolean>` — въпросът на един ред в
// извикващия код, без state и без модал в JSX-а му:
//
//   if (!(await confirm({ intent: "danger", title, explanation, confirmLabel }))) return;
//
// Хостът е `modal.confirm` от `App.useApp()` (React 19: статичният
// Modal.confirm не рендира) — antd слой, който `hasOpenOverlay` вижда; цялото
// му съдържание е `ConfirmPanel` през `modalRender`. `action` тече с въртящ се
// основен бутон и затваря диалога, когато приключи (отхвърлено обещание го
// оставя отворен — като antd onOk).
import { useCallback } from "react";
import { App } from "antd";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import { confirmDialogWidth } from "./confirmLayout";
import { ConfirmPanel } from "./ConfirmPanel";
import type { ConfirmOptions } from "./confirmTypes";

export const useConfirm = () => {
  const { modal } = App.useApp();
  const { isMobile, isTablet } = useViewport();
  const width = confirmDialogWidth({ isMobile, isTablet });

  return useCallback(
    ({ action, ...content }: ConfirmOptions): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        let settled = false;
        let running = false;
        const settle = (confirmed: boolean) => {
          if (settled) return;
          settled = true;
          instance.destroy();
          resolve(confirmed);
        };
        const render = (busy: boolean) => (
          <ConfirmPanel
            {...content}
            busy={busy}
            isActive={() => !settled}
            onCancel={() => settle(false)}
            onConfirm={() => void confirmNow()}
          />
        );
        const confirmNow = async () => {
          if (settled || running) return;
          if (!action) {
            settle(true);
            return;
          }
          running = true;
          instance.update({ maskClosable: false, modalRender: () => render(true) });
          try {
            await action();
            settle(true);
          } catch {
            // остава отворен, за да може да се опита пак или да се откаже
            instance.update({ maskClosable: true, modalRender: () => render(false) });
          } finally {
            running = false;
          }
        };
        const instance = modal.confirm({
          icon: null,
          footer: null,
          centered: true,
          closable: false,
          keyboard: false,
          maskClosable: true,
          autoFocusButton: null,
          width,
          style: modalWrapStyle,
          styles: { mask: modalStyles.mask },
          modalRender: () => render(false),
          // клик по фона (докато `action` тече, фонът не затваря)
          onCancel: () => settle(false),
        });
      }),
    [modal, width],
  );
};

export default useConfirm;
